import React, { useRef } from 'react';
import { apiClient } from '../utils/apiClient';
import { Box, Typography, Stack } from "@mui/material";
import { useCallback, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorDialog, RequestErrorMessage, requestErrors } from "./declarations/errors";
import { CustomDialog } from "./CustomDialog";
import './Paddle.css';
import MenuBar from "./MenuBar";
import Footer from "./Footer";

declare global {
  interface Window {
    Paddle: any;
  }
}

interface TransactionData {
  id: string;
  customer_name: string;
  customer_email: string;
  company_name?: string;
  invoice_number: string;
  items: Array<{
    name: string;
    quantity: number;
    price: number;
  }>;
  discounts?: Array<{
    name: string;
    amount: number;
  }>;
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
}

function PaddleInvoice() {
  const [transactionData, setTransactionData] = useState<TransactionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorDialog, setErrorDialog] = useState<ErrorDialog>();
  const [userClickedPayment, setUserClickedPayment] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const transactionId = searchParams.get('_ptxn');
  
  // Remove the _ptxn parameter from URL to prevent Paddle auto-detection
  useEffect(() => {
    if (transactionId) {
      const url = new URL(window.location.href);
      url.searchParams.delete('_ptxn');
      window.history.replaceState({}, '', url.toString());
      console.log("Removed _ptxn parameter from URL to prevent auto-opening");
    }
  }, [transactionId]);

  const handleCheckoutSuccess = useCallback(async (data: any) => {
    console.log("Invoice payment succeeded", data);
    const formattedTotal =
      typeof transactionData?.total === 'number'
        ? transactionData.total.toFixed(2)
        : undefined;

    // Navigate to success page with invoice details
    navigate('/success-invoice', { 
      state: { 
        paymentInfo: {
          name: transactionData?.customer_name,
          company: transactionData?.company_name,
          email: transactionData?.customer_email,
          amount: formattedTotal,
          invoiceNumber: transactionData?.invoice_number,
          otherInformation: undefined,
          paymentAmount: formattedTotal,
          paymentMethod: 'Credit card',
          currency: transactionData?.currency || 'USD',
        }
      } 
    });
    window.Paddle.Checkout.close();
  }, [navigate, transactionData]);

  const handleCheckoutError = useCallback(async (error: any) => {
    console.log("Invoice payment failed", error);
    // Reset the payment flag so user can try again
    setUserClickedPayment(false);
    // Stay on the same page for failed payments
    window.Paddle.Checkout.close();
  }, []);

  // Initialize Paddle on first render with delay
  const paddleInitialized = useRef(false);
  useEffect(() => {
    // Delay Paddle initialization to prevent auto-opening
    const initializePaddle = () => {
      if (typeof window !== 'undefined' && window.Paddle && !paddleInitialized.current) {
        paddleInitialized.current = true;
        
        console.log("Initializing Paddle for invoice payment...");
        
        // Use REACT_APP_PADDLE_ENVIRONMENT if set, otherwise fall back to NODE_ENV
        const paddleEnv = process.env.REACT_APP_PADDLE_ENVIRONMENT || (process.env.NODE_ENV === 'production' ? 'production' : 'sandbox');
        window.Paddle.Environment.set(paddleEnv);
        window.Paddle.Initialize({
          token: process.env.REACT_APP_PADDLE_CLIENT_TOKEN,
          eventCallback: function(data: any) {
            console.log("Paddle event received:", data.name, data);
            switch(data.name) {
              case 'checkout.completed':
                handleCheckoutSuccess(data);
                break;
              case 'checkout.error':
                handleCheckoutError(data);
                break;
              case 'checkout.loaded':
                console.log("Checkout loaded");
                break;
              case 'checkout.closed':
                // Reset the payment flag when checkout is closed so user can try again
                console.log("Checkout closed - resetting payment flag");
                setUserClickedPayment(false);
                break;
              default:
                console.log("Unhandled Paddle event:", data.name);
                break;
            }
          }
        });
        
        console.log("Paddle initialization complete");
      }
    };

    // Delay initialization to prevent auto-opening
    const timer = setTimeout(initializePaddle, 1000);
    
    return () => clearTimeout(timer);
  }, [handleCheckoutSuccess, handleCheckoutError]);

  // Fetch transaction data when component mounts
  useEffect(() => {
    if (!transactionId) {
      setErrorDialog({
        title: 'Invalid Transaction',
        message: 'No transaction ID provided. Please use the link from your invoice email.'
      });
      setLoading(false);
      return;
    }

    const fetchTransactionData = async () => {
      try {
        const response = await apiClient.post("/get_transaction_details", {
          transaction_id: transactionId
        });
        console.log("Transaction data received:", response.data);
        setTransactionData(response.data);
        setLoading(false);
      } catch (e: any) {
        setErrorDialog(requestErrors[e.message as RequestErrorMessage] || {
          title: 'Error',
          message: 'Failed to load transaction details. Please try again or contact support.'
        });
        setLoading(false);
      }
    };

    fetchTransactionData();
  }, [transactionId]);

  const handleContinueToPayment = () => {
    if (window.Paddle && transactionId && !userClickedPayment) {
      console.log("User clicked Continue to Payment - Opening Paddle checkout for transaction:", transactionId);
      setUserClickedPayment(true);
      window.Paddle.Checkout.open({
        transactionId: transactionId
      });
    } else {
      console.log("Payment button clicked but conditions not met:", {
        paddleReady: !!window.Paddle,
        hasTransactionId: !!transactionId,
        alreadyClicked: userClickedPayment
      });
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div>
        <MenuBar />
        <Box sx={{
          py: 4,
          mb: 8,
          maxWidth: { xs: '95%', md: '90%', lg: '85%' },
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          textAlign: 'center'
        }}>
          <Typography variant="h6">Loading invoice details...</Typography>
        </Box>
        <Footer />
      </div>
    );
  }

  if (!transactionData) {
    return (
      <div>
        <MenuBar />
        <Box sx={{
          py: 4,
          mb: 8,
          maxWidth: { xs: '95%', md: '90%', lg: '85%' },
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          textAlign: 'center'
        }}>
          <Typography variant="h6" color="error">
            Unable to load invoice details
          </Typography>
        </Box>
        <Footer />
        <CustomDialog
          opened={!!errorDialog}
          title={errorDialog?.title || ''}
          body={errorDialog?.message || ''}
        />
      </div>
    );
  }

  return (
    <div>
      <MenuBar />
      <Box sx={{
        py: 4,
        mb: 8,
        maxWidth: { xs: '95%', md: '90%', lg: '85%' },
        mx: 'auto',
        px: { xs: 2, sm: 3 }
      }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Invoice Payment
          </Typography>
        </Box>

        {/* Invoice Details */}
        <Stack spacing={3} sx={{ mb: 3 }}>
          {/* Customer Information */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            <Typography variant="body1">
              <strong>Name:</strong> {transactionData.customer_name}
            </Typography>
            <Typography variant="body1">
              <strong>Email:</strong> {transactionData.customer_email}
            </Typography>
            {transactionData.company_name && (
              <Typography variant="body1">
                <strong>Company:</strong> {transactionData.company_name}
              </Typography>
            )}
          </Box>

          {/* Invoice Information */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Invoice Details
            </Typography>
            <Typography variant="body1">
              <strong>Invoice Number:</strong> {transactionData.invoice_number}
            </Typography>
          </Box>

          {/* Discounts */}
          {transactionData.discounts && transactionData.discounts.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Discounts
              </Typography>
              {transactionData.discounts.map((discount, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body1">
                    {discount.name}
                  </Typography>
                  <Typography variant="body1" color="success.main">
                    -{formatCurrency(discount.amount, transactionData.currency)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Totals */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Total
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1">
                {formatCurrency(transactionData.subtotal, transactionData.currency)}
              </Typography>
            </Box>
            {transactionData.tax > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Tax:</Typography>
                <Typography variant="body1">
                  {formatCurrency(transactionData.tax, transactionData.currency)}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6"><strong>Total:</strong></Typography>
              <Typography variant="h6">
                <strong>{formatCurrency(transactionData.total, transactionData.currency)}</strong>
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* Payment Button */}
        <Box sx={{ textAlign: 'center' }}>
          <button 
            type="button" 
            className="submit-button"
            onClick={handleContinueToPayment}
          >
            Continue to Payment
          </button>
        </Box>
      </Box>
      <Footer />

      <CustomDialog
        opened={!!errorDialog}
        title={errorDialog?.title || ''}
        body={errorDialog?.message || ''}
      />
    </div>
  );
}

export default PaddleInvoice;
